<!--
  The "set scale first" strip, shared by the steps whose tool is blocked by an
  unscaled page. Renders nothing when every selected page has a scale.
  It belongs ABOVE the step's primary button: the scale is a precondition for that
  tool, so the warning has to be read before the button, not after it.
-->
<script>
  import { isScaled } from "./derive.js";
  import { sheetNumber } from "./sheet-label.js";
  import { joinList } from "./labels.js";

  export let pages;
  export let selectedPageList;
  export let visibleIndices;
  /** What this step can't do yet, e.g. "frames can be located". */
  export let action;

  // Names a page for the strip. The sheet number off the title block is how
  // drafters identify a page, so it wins when there is one; a page without one
  // falls back to its position, the way the chips do.
  //
  // Two forms, because the "#" is only doing work on its own. Standing alone,
  // "page #3" marks 3 as a reference to one page rather than a count of them. In a
  // list the plural "pages" prefix already establishes that, so the symbols would
  // just be noise: "pages 2, 3, and 4".
  $: pageRef = (id) => sheetNumber(pages[id]) || `${visibleIndices[id] + 1}`;
  $: pageName = (id) => sheetNumber(pages[id]) || `page #${visibleIndices[id] + 1}`;

  // Listed in page order, never selection order: the warning is read against the
  // drawing set, so "pages 2, 3, and 4" has to match the sequence on screen rather
  // than the order they happened to be clicked. Sorting a filtered copy —
  // selectedPageList is the caller's array and stays untouched.
  $: unscaled = selectedPageList
    .map((p) => p.id)
    .filter((id) => !isScaled(pages[id]))
    .sort((a, b) => visibleIndices[a] - visibleIndices[b]);

  $: message = !unscaled.length
    ? null
    : unscaled.length === 1
      ? `Set scale on ${pageName(unscaled[0])} before ${action}.`
      : `Set scale on pages ${joinList(unscaled.map(pageRef))} before ${action}.`;
</script>

{#if message}
  <div class="fx-scale-warning">{message}</div>
{/if}

<style lang="scss">
  .fx-scale-warning {
    font-size: 12px;
    padding: 6px 8px;
    border-radius: 4px;
    background: var(--fx-amber-bg);
    border: 1px solid var(--fx-amber-border);
    color: var(--fx-amber-text);
  }
</style>
